import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HelpCircle,
  BookOpen,
  Mail,
  Sparkles,
  ChevronRight,
  PlayCircle,
  Shield,
  Briefcase,
  Headphones,
  Download,
  FileText,
} from 'lucide-react';
import { resetOnboarding } from '@/components/onboarding/OnboardingTour';
import { useAuth } from '@/hooks/useAuth';
import type { TFunction } from 'i18next';

const ReactMarkdown = lazy(() => import('react-markdown'));

export const Route = createFileRoute('/_app/help')({
  component: HelpPage,
});

type Role = 'admin' | 'makler' | 'call_center';

function HelpPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const userRole = (profile?.role ?? 'makler') as Role;
  const [tab, setTab] = useState<Role>(userRole);

  // Non-admin users see only their own role tab. Admins see all three so
  // they can quickly answer staff questions about other roles' workflows.
  const visibleRoles: Role[] = userRole === 'admin' ? ['admin', 'makler', 'call_center'] : [userRole];

  const sections = useMemo(() => buildSections(t, tab), [t, tab]);
  const faqs = useMemo(() => buildFaqs(t, userRole), [t, userRole]);

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-display text-2xl text-ink">
          {t('help.title', 'Hilfe & Support')}
        </h2>
        <p className="text-sm text-ink-3">
          {t('help.subtitle', 'Anleitungen pro Rolle, FAQ und KI-Assistent.')}
        </p>
      </header>

      {/* Quick links */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card icon={<PlayCircle className="h-5 w-5" />} title={t('help.tour.title', 'Onboarding-Tour')}>
          <button
            type="button"
            onClick={() => {
              resetOnboarding();
              window.dispatchEvent(new Event('4m:open-tour'));
            }}
            className="inline-flex items-center gap-1 text-sm font-medium text-ink hover:underline"
          >
            {t('help.tour.cta', 'Tour starten')} <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </Card>
        <Card icon={<Sparkles className="h-5 w-5" />} title={t('help.ai.title', 'KI-Assistent Zoki')}>
          <p>
            {t(
              'help.ai.body',
              'Klicken Sie auf das goldene Bot-Icon unten rechts. Zoki sucht Klienten, erstellt Termine, liefert Statistiken — alles per Anweisung in natürlicher Sprache.',
            )}
          </p>
        </Card>
        <Card icon={<Mail className="h-5 w-5" />} title={t('help.support.title', 'Support kontaktieren')}>
          <a
            href="mailto:office@4m-immo.at"
            className="inline-flex items-center gap-1 text-sm font-medium text-ink hover:underline"
          >
            office@4m-immo.at <ChevronRight className="h-3.5 w-3.5" />
          </a>
        </Card>
      </div>

      {/* Downloadable handbook — role-specific PDF.
          Admin can see all three (Maker, CallCenter, full handbook). */}
      <div className="fm-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <FileText className="h-5 w-5 text-gold" />
          <div>
            <h3 className="font-display text-lg text-ink">
              {t('help.handbook.title', 'Ihr persönliches Handbuch')}
            </h3>
            <p className="text-xs text-ink-3">
              {t(
                'help.handbook.subtitle_role',
                'Ausführliches, bebildertes PDF mit Screenshots — Schritt für Schritt für Ihre Rolle. Aktualisiert Juli 2026.',
              )}
            </p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {(userRole === 'makler' || userRole === 'admin') && (
            <a
              href="/docs/4M-Makler-Handbuch-DE.pdf"
              download
              className="group flex items-start gap-3 rounded-md border border-line bg-paper-2 p-4 transition hover:border-gold hover:bg-paper-3"
            >
              <Download className="mt-0.5 h-5 w-5 flex-shrink-0 text-ink-3 group-hover:text-gold" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">
                  {t('help.handbook.makler_title', 'Makler-Handbuch (DE)')}
                </p>
                <p className="text-xs text-ink-3">
                  {t('help.handbook.makler_meta', 'Vollständige Anleitung · mit Screenshots · Zeit-Spar-Tipps')}
                </p>
              </div>
            </a>
          )}
          {(userRole === 'call_center' || userRole === 'admin') && (
            <a
              href="/docs/4M-CallCenter-Handbuch-DE.pdf"
              download
              className="group flex items-start gap-3 rounded-md border border-line bg-paper-2 p-4 transition hover:border-gold hover:bg-paper-3"
            >
              <Download className="mt-0.5 h-5 w-5 flex-shrink-0 text-ink-3 group-hover:text-gold" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">
                  {t('help.handbook.cc_title', 'Call-Center-Handbuch (DE)')}
                </p>
                <p className="text-xs text-ink-3">
                  {t('help.handbook.cc_meta', 'Workflow · SMS-Vorlagen · mit Screenshots · Übergabe an Makler')}
                </p>
              </div>
            </a>
          )}
          {userRole === 'admin' && (
            <a
              href="/docs/4M-CRM-Handbuch-DE.pdf"
              download
              className="group flex items-start gap-3 rounded-md border border-line bg-paper-2 p-4 transition hover:border-gold hover:bg-paper-3"
            >
              <Download className="mt-0.5 h-5 w-5 flex-shrink-0 text-ink-3 group-hover:text-gold" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">
                  {t('help.handbook.full_title', 'Vollständiges CRM-Handbuch (DE)')}
                </p>
                <p className="text-xs text-ink-3">
                  {t('help.handbook.full_meta', 'Admin-Übersicht · alle Module · mit Screenshots')}
                </p>
              </div>
            </a>
          )}
        </div>
      </div>

      {/* Per-role guide */}
      <div className="fm-card p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 font-display text-lg text-ink">
            <BookOpen className="h-4 w-4 text-ink-3" />
            {t('help.guide.title', 'Bedienungsanleitung pro Rolle')}
          </h3>
          <p className="hidden text-xs text-ink-3 md:block">
            {t('help.guide.your_role', 'Ihre Rolle:')}{' '}
            <span className="font-mono uppercase tracking-wider text-ink-2">{userRole}</span>
          </p>
        </div>

        {visibleRoles.length > 1 && (
          <div className="mb-5 inline-flex flex-wrap gap-1 rounded-md bg-paper-2 p-1">
            {visibleRoles.includes('admin') && (
              <RoleTab active={tab === 'admin'} onClick={() => setTab('admin')} icon={<Shield className="h-3.5 w-3.5" />}>
                {t('help.tabs.admin', 'Admin')}
              </RoleTab>
            )}
            {visibleRoles.includes('makler') && (
              <RoleTab active={tab === 'makler'} onClick={() => setTab('makler')} icon={<Briefcase className="h-3.5 w-3.5" />}>
                {t('help.tabs.makler', 'Makler')}
              </RoleTab>
            )}
            {visibleRoles.includes('call_center') && (
              <RoleTab
                active={tab === 'call_center'}
                onClick={() => setTab('call_center')}
                icon={<Headphones className="h-3.5 w-3.5" />}
              >
                {t('help.tabs.call_center', 'Call-Center')}
              </RoleTab>
            )}
          </div>
        )}

        <ol className="space-y-5">
          {sections.map((sec, i) => (
            <li key={sec.id} className="border-s-2 border-[hsl(var(--brand-gold))]/40 ps-4">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-3">
                {t('help.guide.step', 'Schritt')} {i + 1}
              </p>
              <h4 className="font-display text-base text-ink">{sec.title}</h4>
              <Suspense fallback={<p className="mt-2 text-sm text-ink-2">{sec.body}</p>}>
                <div className="prose-sm mt-2 max-w-none text-sm text-ink-2">
                  <ReactMarkdown>{sec.body}</ReactMarkdown>
                </div>
              </Suspense>
            </li>
          ))}
        </ol>
      </div>

      {/* FAQ */}
      <div className="fm-card p-6">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-ink">
          <BookOpen className="h-4 w-4 text-ink-3" />
          {t('help.faq.title', 'Häufig gestellte Fragen')}
        </h3>
        <ul className="space-y-2">
          {faqs.map((f, i) => (
            <li key={i} className="rounded-md border border-line bg-paper-2 p-3">
              <details>
                <summary className="cursor-pointer font-medium text-ink">{f.q}</summary>
                <p className="mt-2 text-sm text-ink-2">{f.a}</p>
              </details>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-xs text-ink-3">
        <HelpCircle className="me-1 inline h-3 w-3" />
        {t(
          'help.foot',
          'Diese Anleitung wird laufend aktualisiert. Bei Lücken oder Fragen wenden Sie sich an office@4m-immo.at oder schreiben Sie in den Support-Chat („Unterstützung").',
        )}
      </p>
    </section>
  );
}

interface Section {
  id: string;
  title: string;
  body: string;
}

function buildSections(t: TFunction, role: Role): Section[] {
  if (role === 'admin') {
    return [
      {
        id: 'admin-1',
        title: t('help.admin.s1.title', 'Anmeldung & Rollen'),
        body: t(
          'help.admin.s1.body',
          'Melden Sie sich auf **/login** mit dem Tab **Admin** an. Admins haben Vollzugriff auf alle Klienten, Immobilien und Termine — unabhängig vom zuweisenden Makler. Verwenden Sie den **Profile-Tab in den Einstellungen**, um Ihren Avatar, Ihre Telefonnummer und E-Mail zu pflegen.',
        ),
      },
      {
        id: 'admin-2',
        title: t('help.admin.s2.title', 'Makler & Call-Center anlegen'),
        body: t(
          'help.admin.s2.body',
          'Im **Admin-Bereich → Agenten** legen Sie neue Mitarbeiter:innen an. Das System generiert eine 8-stellige **Agent-ID**; das Initial-Passwort ist die Agent-ID in Großbuchstaben. Notieren Sie sie und geben Sie sie persönlich weiter — sie wird danach nicht mehr angezeigt. Beim ersten Login wird automatisch zu einer sicheren scrypt-Hashes umgeschaltet.',
        ),
      },
      {
        id: 'admin-3',
        title: t('help.admin.s3.title', 'Bulk-Import von Klienten'),
        body: t(
          'help.admin.s3.body',
          'Auf **/clients → CSV importieren**. Spalten werden automatisch erkannt (Vor-/Nachname, Telefon, E-Mail, Plattform, Notizen). Duplikate werden anhand der **Telefonnummer im E.164-Format** übersprungen. Nach dem Import erhalten Sie eine Zusammenfassung: importiert, übersprungen, fehlgeschlagen.',
        ),
      },
      {
        id: 'admin-4',
        title: t('help.admin.s4.title', 'Eigentums-Transfers freigeben'),
        body: t(
          'help.admin.s4.body',
          'Wenn ein Makler einen Klienten oder eine Immobilie an eine andere Person übertragen möchte, stellt er einen **Transfer-Antrag**. Sie sehen alle offenen Anträge in **/transfers**. Mit **Genehmigen** wird die Zuweisung sofort umgeschaltet; mit **Ablehnen** bleibt alles beim Antragsteller. Jede Aktion ist im Audit-Log protokolliert.',
        ),
      },
      {
        id: 'admin-5',
        title: t('help.admin.s5.title', 'Audit-Log & Sicherheit'),
        body: t(
          'help.admin.s5.body',
          'Unter **/audit-log** sehen Sie jede CRUD-Änderung an Klienten, Immobilien, Terminen und Anboten — mit altem und neuem Wert. Filterbar nach Tabelle und Zeitraum, exportierbar als CSV. **/security** sammelt automatisch Auffälligkeiten (Massen-Änderungen, Telefonnummern-Löschungen, ungewöhnliche Selbst-Zuweisungen).',
        ),
      },
      {
        id: 'admin-6',
        title: t('help.admin.s6.title', 'Analytik & KI-Statistik'),
        body: t(
          'help.admin.s6.body',
          'Auf **/analytics** finden Sie Performance-Diagramme, Termine pro Monat, Anboten pro Monat, Makler-Ranking. **Zoki** kann Ihnen ad-hoc Statistiken liefern — z.B. „Zeig mir Statistiken für ESJ59OJ5" oder „Wie viele aktive Immobilien?". Er ruft die Tools eigenständig auf.',
        ),
      },
      {
        id: 'admin-7',
        title: t('help.admin.s7.title', 'E-Mail-Marketing & Kampagnen'),
        body: t(
          'help.admin.s7.body',
          'Auf **/email-marketing** verwalten Sie Vorlagen, Empfänger:innen-Listen und Kampagnen. Bounces und Spam-Beschwerden werden automatisch über den Resend-Webhook erfasst und betroffene E-Mail-Adressen werden auf **bounced/unsubscribed** gesetzt — keine doppelten Sendungen.',
        ),
      },
      {
        id: 'admin-8',
        title: t('help.admin.s8.title', 'Datensicherung & DSGVO'),
        body: t(
          'help.admin.s8.body',
          'D1-Backups laufen täglich um 03:00 UTC nach R2 (`4m-backups/d1-staging/`). Aufbewahrungsfrist 30 Tage automatisch durch R2-Lifecycle-Regel. Archivierte Chat-Nachrichten werden nach 90 Tagen physisch gelöscht inkl. R2-Anhängen — DSGVO-konform.',
        ),
      },
    ];
  }

  if (role === 'makler') {
    return [
      {
        id: 'makler-1',
        title: t('help.makler.s1.title', 'Ihr Tag beginnt auf /dashboard'),
        body: t(
          'help.makler.s1.body',
          'Login mit **Agent-ID** + Initial-Passwort (Agent-ID in Großbuchstaben → in den Einstellungen sofort ändern). Auf **/dashboard** sehen Sie **alles auf einen Blick**: heutige Termine, fällige Aktionen, neue Leads, persönliche KPIs. **Klicken Sie auf eine KPI-Karte → springen Sie direkt in die gefilterte Liste.** *Spart pro Morgen 15–20 Minuten — kein Zusammensuchen aus drei Tabs mehr.*',
        ),
      },
      {
        id: 'makler-2',
        title: t('help.makler.s2.title', 'Klient in 30 Sekunden anlegen'),
        body: t(
          'help.makler.s2.body',
          'Auf **/clients → „Neuer Klient"**. **Pflichtfelder nur 4** — Anrede, Name, Telefon, Typ. Telefon wird automatisch ins E.164-Format umgewandelt (kein manuelles „+43"-Tippen mehr). **Duplikat-Schutz** prüft die Nummer sofort gegen alle 9.000+ vorhandenen Klienten und warnt, bevor Sie auf „Anlegen" klicken. Bei E-Mail-Eintrag versendet das System automatisch eine **branded Begrüßungs-E-Mail** mit Ihrem Foto und Ihrer Signatur.',
        ),
      },
      {
        id: 'makler-3',
        title: t('help.makler.s3.title', 'Aufnahme — vom Termin zum Vermittlungsauftrag in 3 Klicks'),
        body: t(
          'help.makler.s3.body',
          '**1. Termin** auf **/appointments** anlegen (Typ **aufnahme**) → SMS mit Datum + Adresse + Ihrem Foto geht **sofort** an den Klienten. **2. Vor Ort** öffnen Sie das Klient-Profil am Handy, fügen die Immobilie hinzu, Fotos lädt das Telefon automatisch herunter (WebP komprimiert → kein Speicher-Stress). **3. Vermittlungsauftrag** generieren — PDF wird mit **WKO-Nebenkosten-Anlage** + DSGVO-Klausel erstellt, **Magic-Link** geht per SMS, Klient unterschreibt am Handy mit Finger oder Stift. Sobald die Unterschrift kommt: Immobilie wechselt automatisch von **Lead → Aufnahme → Aktiv** und ist sofort marktreif. *Diese 3 Schritte sparen Ihnen ~90 Minuten Papierkram pro Aufnahme.*',
        ),
      },
      {
        id: 'makler-4',
        title: t('help.makler.s4.title', 'Interessenten-Matching — Käufer trifft Objekt automatisch'),
        body: t(
          'help.makler.s4.body',
          'Auf **/interessenten** sehen Sie alle Käufer mit Suchprofil. **„Matches"-Knopf** durchsucht in Echtzeit alle aktiven Immobilien und filtert nach Bezirk, Preis-Range, m² und Zimmer-Zahl. Sie bekommen eine sortierte Liste mit **Match-Score**. **Termin „Besichtigung"** anlegen → automatischer **Besichtigungsschein-PDF** mit DSGVO-Klausel + Magic-Link zur Unterschrift. *Statt 200 Klienten manuell durchzugehen, finden Sie die richtigen 5 in 10 Sekunden.*',
        ),
      },
      {
        id: 'makler-5',
        title: t('help.makler.s5.title', 'Kaufanbot — 5 Schritte, eine PDF, ein Klick zur Unterschrift'),
        body: t(
          'help.makler.s5.body',
          'Auf **/offers → „Neues Kaufanbot"**: **1.** Käufer + Immobilie wählen. **2.** Preis + Bedingungen. **3.** Provision (automatisch nach 4M-Tarif vorbelegt, anpassbar). **4.** Vorschau → ausführliches Kaufanbot-PDF wird live gerendert. **5.** Versenden — E-Mail + SMS-Magic-Link an alle Käufer (auch bei mehreren Käufern, jeder unterschreibt einzeln). Status-Pipeline: **Entwurf → Gesendet → Akzeptiert / Abgelehnt → Unterschrieben → Notar geplant → Notariell verkauft**. *Mit dieser Pipeline behalten Sie 30+ parallele Anboten im Überblick.*',
        ),
      },
      {
        id: 'makler-6',
        title: t('help.makler.s6.title', 'Automatische Erinnerungen + Morgen-Briefing'),
        body: t(
          'help.makler.s6.body',
          '**Sie müssen keinen Termin mehr bestätigen** — das System schickt **2h vor jedem Termin** automatisch eine SMS-Erinnerung an den Klienten und an Sie. **60 Minuten vor dem Termin** klingelt Ihre Glocke + Push-Benachrichtigung. **Jeden Morgen 06:00** erhalten Sie ein **Briefing per E-Mail**: heutige Termine, offene Anboten, fällige Followups, ungelesene Chat-Nachrichten. *Sie öffnen morgens die Mails, wissen alles für heute — ohne den CRM auch nur einmal angetippt zu haben.*',
        ),
      },
      {
        id: 'makler-7',
        title: t('help.makler.s7.title', 'Team-Chat — schneller als E-Mail, sicherer als WhatsApp'),
        body: t(
          'help.makler.s7.body',
          '**Glocke oben rechts** öffnet Chat. 1:1 oder Gruppen, **Bilder + PDFs + Audio** als Anhang (Drag-and-drop), **@Erwähnungen** mit Push, **Sprachnachrichten** (Mikrofon-Knopf), **Diktat zum Tippen** (Audio-Wellen-Knopf — Sie diktieren, Text erscheint), **Antwort auf Nachricht** mit Vorschau, **Stumm-Schalten** pro Gespräch. Fixe Gruppe **„Unterstützung"** für direkte Fragen an die Geschäftsleitung. *Alle Anhänge sind verschlüsselt in unserem R2-Speicher — nicht in WhatsApp-Servern, nicht bei Meta.*',
        ),
      },
      {
        id: 'makler-8',
        title: t('help.makler.s8.title', 'Property-Sharing — Listing in 8 Klicks weiterleiten'),
        body: t(
          'help.makler.s8.body',
          'Auf jeder Immobilie **„Teilen"-Knopf** → öffentlicher Magic-Link wird erstellt (Foto-Galerie + KPI + Ihr Kontakt, **keine Verkäuferdaten**). Sie schreiben einen kurzen Begleittext (auto-vorgenerierter Vorschlag), wählen die Plattform — **WhatsApp · Viber · Telegram · SMS · E-Mail · Facebook · LinkedIn · X · Instagram**. Klick öffnet die Plattform mit Ihrem Text + Link bereits eingetragen. *Anfragen vom öffentlichen Link landen automatisch als „Interessent" in Ihrem CRM.*',
        ),
      },
      {
        id: 'makler-9',
        title: t('help.makler.s9.title', 'KI-Assistent Zoki — Ihr persönlicher CRM-Bot'),
        body: t(
          'help.makler.s9.body',
          'Goldener Bot-Knopf unten rechts. Zoki versteht natürliche Sprache: **„Such mir alle Käufer im 9. Bezirk bis 600k"** · **„Erstelle Termin morgen 10:00 mit Hr. Müller"** · **„Wer ist mein letzter Aufnahme-Klient?"** · **„Beschreibe diese Immobilie attraktiv"** (Vision-Tool für Foto-Analyse). Zoki ruft die richtigen Tools selbst auf — keine manuelle Suche, keine Filter-Klicks. *Sparen Sie 20–30 Minuten pro Tag bei Routine-Recherchen.*',
        ),
      },
      {
        id: 'makler-10',
        title: t('help.makler.s10.title', 'Mobile-First — überall arbeiten, nicht nur im Büro'),
        body: t(
          'help.makler.s10.body',
          'Die Web-App ist **mobile-optimiert** (kein Apple-App-Store nötig). Auf iPhone Safari → **Teilen → „Zum Home-Bildschirm"** → PWA-Modus mit Icon, Push-Benachrichtigungen, Vollbild. Termin am Auto? Klient-Profil am Auto-Display? Aufnahme-Fotos vom Handy? Alles geht — sogar offline-cached bei schlechter Verbindung. *Sie sind nicht mehr ans Büro gefesselt — Ihr CRM ist immer in der Hosentasche.*',
        ),
      },
      {
        id: 'makler-11',
        title: t('help.makler.s11.title', 'Persönliche Daten — Sie sind die Marke'),
        body: t(
          'help.makler.s11.body',
          'In **Einstellungen → Profil** pflegen Sie Ihr Foto, Telefon, E-Mail, Titel (Dipl.-Ing., Mag., …). Diese Daten erscheinen in: **jeder SMS** (Absender-Vorlage), **jedem PDF** (Vermittlungsauftrag-Footer), **Begrüßungs-E-Mails** an Ihre neuen Klienten, **öffentlichen Listing-Seiten** beim Property-Sharing. **Einstellungen → Erscheinungsbild** lässt Sie 5 Voreinstellungen + freie Akzentfarbe + Hintergrundbild wählen — der CRM passt sich Ihrem persönlichen Stil an.',
        ),
      },
    ];
  }

  // call_center
  return [
    {
      id: 'cc-1',
      title: t('help.cc.s1.title', 'Ihre Rolle — der erste Eindruck zählt'),
      body: t(
        'help.cc.s1.body',
        'Sie sind die **erste Stimme**, die ein potentieller Klient von 4M Immobilien hört. Login im Tab **Makler** mit Ihrer Agent-ID. Sie sehen die Klienten-Liste zur Lead-Erfassung und Erstkontaktaufnahme. Was Sie eingeben, landet **direkt** in den Händen des zuständigen Makler-Teams — keine doppelte Eingabe, kein Excel-Pingpong. *Diese Geschwindigkeit ist Ihr Wettbewerbsvorteil gegenüber der Konkurrenz.*',
      ),
    },
    {
      id: 'cc-2',
      title: t('help.cc.s2.title', 'Lead in 60 Sekunden aufnehmen'),
      body: t(
        'help.cc.s2.body',
        'Bei eingehendem Anruf (Mail, willhaben, Empfehlung) auf **/clients → „Neuer Klient"**: Vorname, Nachname, Telefon, Plattform und kurze Notiz. Telefon wird automatisch ins **E.164-Format** umgewandelt — Sie tippen einfach was Sie hören. **Duplikat-Check** läuft sofort: falls die Nummer bereits existiert, schlägt das System vor, den bestehenden Klienten zu öffnen statt einen Doppel-Eintrag zu erzeugen. *Spart pro Anruf ~2 Minuten Suchen nach Bestandsklienten.*',
      ),
    },
    {
      id: 'cc-3',
      title: t('help.cc.s3.title', 'Standard-Makler — Sie buchen Termine in seinem Namen'),
      body: t(
        'help.cc.s3.body',
        'Wenn der Admin Ihrem Profil einen **Standard-Makler** zugewiesen hat (in Einstellungen sichtbar), werden Termine, die Sie anlegen, **automatisch diesem Makler zugewiesen**. Sie müssen nicht jedes Mal aussuchen — der Workflow ist auf Sie zugeschnitten. *3 Sekunden statt 15 Sekunden pro Termin gespart.*',
      ),
    },
    {
      id: 'cc-4',
      title: t('help.cc.s4.title', 'Aufnahme-Termin anlegen — voll integriert'),
      body: t(
        'help.cc.s4.body',
        'Auf **/appointments → „Neuer Termin"**: Klient + Datum/Uhrzeit + Typ (meist **Aufnahme** oder **Besichtigung**) wählen. Das System fragt einen **Trockenlauf** (dryRun) ab: gibt es bereits einen Termin mit diesem Klienten am selben Tag? Dann erscheint eine Warnung. Sobald Sie auf „Anlegen" klicken: **(1)** SMS-Bestätigung geht **sofort** an den Klienten mit Adresse + Foto des Makler-s, **(2)** in-app Benachrichtigung + Push an den Makler, **(3)** Kalender-Eintrag wird erstellt. *Drei Aktionen, ein Klick — kein E-Mail-Roundtrip mit dem Makler mehr nötig.*',
      ),
    },
    {
      id: 'cc-5',
      title: t('help.cc.s5.title', 'SMS senden — fertige Vorlagen, Klient-Variablen'),
      body: t(
        'help.cc.s5.body',
        'Auf jedem Klient: **Drei-Punkte-Menü → „Nachricht senden"**. Sie können freien Text schreiben **oder** eine der Vorlagen wählen: **Termin-Bestätigung**, **Bewertungs-Anfrage**, **Follow-up**, **WKO-Info**. Personalisierungs-Platzhalter wie `{{first_name}}`, `{{appointment_date}}` werden automatisch ersetzt. Versendet über **Twilio** (offizielle WhatsApp + SMS API) — keine Privat-Handy-Nutzung mehr. *Pro Tag: 20+ SMS in einer halben Stunde, mit Branding und Tracking.*',
      ),
    },
    {
      id: 'cc-6',
      title: t('help.cc.s6.title', 'Klient-Historie — Sie sehen, was vorher passiert ist'),
      body: t(
        'help.cc.s6.body',
        'Klick auf Klient → **Verlauf**-Tab zeigt: alle vergangenen Termine, alle SMS-Nachrichten (mit Lese-Status), alle Notizen, alle E-Mails. Wenn Sie zurückrufen, wissen Sie: „Hr. Müller hat letzten Mittwoch eine Besichtigung in der Berggasse abgesagt — ich erwähne das proaktiv und frage nach einem Ersatztermin." *Klient fühlt sich erinnert, nicht abgespeist.*',
      ),
    },
    {
      id: 'cc-7',
      title: t('help.cc.s7.title', 'Team-Chat — Frage an Makler in 5 Sekunden'),
      body: t(
        'help.cc.s7.body',
        'Glocke oben rechts. Statt E-Mail oder Anruf: schreiben Sie dem Makler direkt. **Anhänge** (Foto vom Klient-Termin-Wunsch, PDF vom Inserat), **Sprachnachrichten** (Mikrofon-Knopf — Sie reden, der Makler hört später), **@Erwähnung** mit Push-Benachrichtigung. Fixe Gruppe **„Unterstützung"** für Fragen an die Geschäftsleitung. *Kein „Hat Maja schon gesehen?"-Warten mehr — Sie sehen den Lese-Status.*',
      ),
    },
    {
      id: 'cc-8',
      title: t('help.cc.s8.title', 'KI-Assistent Zoki — Ihre digitale Hand'),
      body: t(
        'help.cc.s8.body',
        'Goldener Bot-Knopf unten rechts. Zoki sucht für Sie: **„Such Klient mit Telefon +43699..."** · **„Erstelle Termin morgen 14:00 für Karen mit Frau Müller"** · **„Welche Klienten haben in den letzten 3 Tagen angerufen?"**. Zoki ruft die Tools selbst auf — Sie tippen die Anfrage, Zoki erledigt sie. *Statistik-/Bilanz-Fragen lehnt Zoki für Ihre Rolle automatisch ab — DSGVO-konform.*',
      ),
    },
    {
      id: 'cc-9',
      title: t('help.cc.s9.title', 'Ihre Daten — Sie sind Teil des Teams'),
      body: t(
        'help.cc.s9.body',
        'In **Einstellungen → Profil** pflegen Sie Foto, Telefon, E-Mail — diese Daten erscheinen auf jeder SMS (Absender wird höflich, persönlich, kein anonymes Call-Center-Auftreten). **Einstellungen → Erscheinungsbild** lässt Sie 5 Voreinstellungen + Akzentfarbe + Hintergrund wählen — der CRM passt sich Ihrem Stil an, nicht umgekehrt.',
      ),
    },
  ];
}

function buildFaqs(t: TFunction, role: Role): Array<{ q: string; a: string }> {
  const all: Array<{ q: string; a: string; admin?: true }> = [
    {
      q: t('help.faq.q1', 'Wie lege ich einen neuen Kunden an?'),
      a: t(
        'help.faq.a1',
        'Auf der Seite „Klienten" oben rechts auf „Kunde hinzufügen" klicken. Anrede, Vor- und Nachname, Telefon und Typ (Käufer/Verkäufer) sind Pflicht.',
      ),
    },
    {
      q: t('help.faq.q2', 'Wie sende ich eine SMS-Erinnerung an einen Klienten?'),
      a: t(
        'help.faq.a2',
        'In der Klientenliste auf das Drei-Punkte-Menü → „Nachricht senden". SMS wird über Twilio versendet.',
      ),
    },
    {
      q: t('help.faq.q3', 'Was bedeuten die Phasen-Farben bei Immobilien?'),
      a: t(
        'help.faq.a3',
        'Grau = Lead, Orange = Aufnahme/Pending, Gold = Aktiv, Grün = Verkauft.',
      ),
    },
    {
      // Admin-only: CSV import is gated to admin role.
      q: t('help.faq.q4', 'Wie funktioniert der CSV-Import?'),
      a: t(
        'help.faq.a4',
        'Auf der Klienten-Seite auf „CSV Importieren". Spalten werden automatisch erkannt; Duplikate per Telefon übersprungen.',
      ),
      admin: true,
    },
    {
      q: t('help.faq.q5', 'Wer kann Klienten/Immobilien löschen?'),
      a: t(
        'help.faq.a5',
        'Nur Admins. Andere Rollen sehen die Löschen-Aktion nicht.',
      ),
    },
    {
      q: t('help.faq.q6', 'Wie ändere ich meine Sprache oder das Erscheinungsbild?'),
      a: t(
        'help.faq.a6',
        'In den Einstellungen → Sprache (DE / EN / SR) oder Design (5 Voreinstellungen + freie Anpassung von Akzentfarbe, Hintergrund, Schriftart, Symbol-Stärke). Änderungen synchronisieren über alle Geräte.',
      ),
    },
    {
      q: t('help.faq.q7', 'Was passiert mit gelöschten Klienten?'),
      a: t(
        'help.faq.a7',
        'Soft-Delete: archived_at wird gesetzt, der Datensatz bleibt physisch erhalten. Admins können wiederherstellen.',
      ),
    },
  ];
  return all
    .filter((f) => (f.admin ? role === 'admin' : true))
    .map(({ q, a }) => ({ q, a }));
}

function Card({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="fm-card p-5">
      <div className="mb-2 flex items-center gap-2 text-ink-2">
        {icon}
        <p className="font-display text-base text-ink">{title}</p>
      </div>
      <div className="text-sm text-ink-3">{children}</div>
    </div>
  );
}

function RoleTab({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
        active ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}
